import React, { useCallback, useState, useEffect } from "react";
import { Aurora, Card, PageHeader, Logo, Stepper, PatentPendingBadge } from "./Shell";
import Instructions from "./Instructions";
import { wakeElara } from "../lib/maya";
import { markProgressCheckIn } from "../lib/memberIdentity";
import RecoveryScoreCard from "./RecoveryScoreCard";
import ListenButton from "./ListenButton";

const PROGRESS_FORM_URL = "https://integratedwellness.jotform.com/262077987278072";

// Static "how to read this summary" guidance — module-level so we don't
// allocate a new array on every render of the summary screen.
const HOW_TO_READ_SUMMARY = [
  { strong: "Recovery Score (0-100)", text: "a wellness-only signal — not a diagnosis. It blends your activation drop, mood change, and engagement on a self-report basis. Higher is a stronger session response; it tracks over time as a trend." },
  { strong: "SUDS Δ (negative is good)", text: "the difference between your pre- and post-session distress. Any drop — even 1 or 2 points — is a meaningful nervous-system shift." },
  { strong: "Engagement %", text: "how steadily you tapped (Mode A) or stayed with the dot (Mode B). High % usually correlates with deeper effect." },
  { strong: "Alternation / Consistency (Mode A only)", text: "100% alternation = perfect left-right pattern. High consistency = even rhythm. Together they validate engagement." },
  { strong: "Care-team alert", text: "fires if post-SUDS ≥ 7, SUDS rose ≥ 3 points, or engagement was low on a real session. Your wellness team will see it." },
  { strong: "Wearable timestamps", text: "use the start/end and pre-baseline / post-recovery windows to pull HR/HRV from Apple Health, Oura, Whoop, etc." },
];

// One-line "what to try next in an adjacent domain" tip for each of the
// other 8 wellness domains. Picked by hashing the session id so the same
// session always shows the same tip — but different sessions rotate
// naturally across the 9-domain framework introduced on the Welcome page.
const ADJACENT_DOMAIN_TIPS = [
  {
    n: 1,
    name: "Physical",
    tip: "Add a 5-minute walk outside tomorrow — pair movement with daylight, and your recovery work gets a second wind.",
  },
  {
    n: 2,
    name: "Emotional",
    tip: "Name one feeling out loud today — research keeps showing that labeling it quiets it.",
  },
  {
    n: 3,
    name: "Social",
    tip: "Send a 30-second voice note to someone who steadies you. Co-regulation is the original calming tool.",
  },
  {
    n: 4,
    name: "Vocational",
    tip: "Block 25 minutes for one task with notifications off — single-tasking is itself a kind of nervous-system rest.",
  },
  {
    n: 5,
    name: "Financial",
    tip: "Spend 5 quiet minutes looking at one money question that's been hovering. Clarity calms.",
  },
  {
    n: 6,
    name: "Spiritual",
    tip: "Sit for 3 minutes with no phone, no agenda. Whatever 'spiritual' means to you, give it the space.",
  },
  {
    n: 7,
    name: "Environmental",
    tip: "Open a window. Move one piece of clutter. Your space affects your nerves more than you think.",
  },
  {
    n: 8,
    name: "Intellectual",
    tip: "Read 2 pages of something you chose for yourself — not work, not news. Curiosity is restorative.",
  },
];

function pickAdjacentDomain(sessionId) {
  if (!sessionId) return ADJACENT_DOMAIN_TIPS[0];
  // simple stable hash → index 0..7
  let h = 0;
  for (let i = 0; i < sessionId.length; i++) {
    h = (h * 31 + sessionId.charCodeAt(i)) >>> 0;
  }
  return ADJACENT_DOMAIN_TIPS[h % ADJACENT_DOMAIN_TIPS.length];
}

const SAVE_LABELS = {
  saving: "Saving…",
  error: "Retry save to Notion",
};

function statValueColor(accent, danger) {
  if (accent) return "var(--accent-cyan)";
  if (danger) return "var(--danger)";
  return "var(--chrome)";
}

export default function SessionSummary({ result, onRestart, stepIndex, steps }) {
  if (!result) return null;
  const s = result.session;
  const notion = result.notion || {};
  const isPartial = !!s.partial;
  const hasPostSuds = s.post_suds !== null && s.post_suds !== undefined;
  const sudsDelta = hasPostSuds ? s.post_suds - s.pre_suds : null;
  const phq4Delta = (s.post_phq4 ?? 0) - (s.pre_phq4 ?? 0);
  const gad7Delta = (s.post_gad7 ?? 0) - (s.pre_gad7 ?? 0);
  const sudsImproved = hasPostSuds && sudsDelta <= 0;

  const summaryTitle = (() => {
    if (isPartial) return "Session stopped.";
    if (sudsImproved) return "Nicely done.";
    return "Session complete.";
  })();
  const summarySubtitle = (() => {
    if (isPartial) {
      return "You ended the session early. Here's a snapshot of what we captured up to that point.";
    }
    if (sudsImproved) {
      return "Your distress eased. Here is a snapshot of the session.";
    }
    return "Your nervous system did the work. Here is a snapshot — including a note to your care team.";
  })();

  return (
    <Aurora>
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <Stepper steps={steps} current={stepIndex} />
        </div>

        <PageHeader
          eyebrow="Step 08 · Session Summary"
          title={summaryTitle}
          subtitle={summarySubtitle}
        />

        {isPartial && (
          <Card
            className="mb-8 border-l-4 fade-up"
            strong
            style={{ borderLeftColor: "var(--accent-cyan)" }}
            data-testid="partial-session-banner"
          >
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-1">
              Partial Session · Stopped Early
            </div>
            <div className="text-ink text-lg font-light leading-snug">
              You skipped the post-session check-in and grounding exit. That's fine — your in-session telemetry is still logged for your wellness team. If you have a few moments now, take 4 slow breaths before moving on.
            </div>
          </Card>
        )}

        <div className="mb-8 -mt-4 fade-up">
          <PatentPendingBadge />
        </div>

        <Instructions
          title="How to read this summary"
          items={HOW_TO_READ_SUMMARY}
        />

        {s.escalation_flag && (
          <Card
            className="mb-8 border-l-4 fade-up"
            strong
            style={{ borderLeftColor: "var(--danger)" }}
            data-testid="escalation-banner"
          >
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--danger)] mb-1">
              Care-team Alert · Triggered
            </div>
            <div className="text-ink text-lg font-light leading-snug">
              Your responses suggest you'd benefit from a check-in. A member of your wellness team has been notified. If you are in immediate danger, please call <span className="font-mono text-cyan-glow">988</span> or your local emergency number.
            </div>
          </Card>
        )}

        <div className="grid sm:grid-cols-3 gap-4 fade-up">
          <Bento label="Mode" value={`Mode ${s.mode}${isPartial ? " · partial" : ""}`} mono />
          <Bento label="Duration" value={`${Math.round(s.duration_seconds || 0)}s`} mono />
          <Bento
            label="Engagement"
            value={`${Math.round((s.engagement_score || 0) * 100)}%`}
            accent
            mono
          />

          <Bento label="SUDS · Pre" value={s.pre_suds} mono />
          <Bento label="SUDS · Post" value={hasPostSuds ? s.post_suds : "—"} mono />
          <Bento
            label="SUDS · Δ"
            value={hasPostSuds ? `${sudsDelta > 0 ? "+" : ""}${sudsDelta}` : "—"}
            accent={sudsImproved}
            danger={hasPostSuds && !sudsImproved && sudsDelta > 0}
            mono
          />

          {!isPartial && s.recovery_score !== null && s.recovery_score !== undefined && (
            <Bento
              label={`Recovery Score${s.recovery_basis === "self-report" ? " · Self-report" : ""}`}
              value={`${s.recovery_score}/100`}
              accent={s.recovery_score >= 60}
              mono
            />
          )}

          {!isPartial && s.pre_phq4 !== null && s.pre_phq4 !== undefined && (
            <Bento label="PHQ-4 · Pre→Post" value={`${s.pre_phq4} → ${s.post_phq4}`} mono />
          )}
          {!isPartial && s.pre_gad7 !== null && s.pre_gad7 !== undefined && (
            <Bento label="GAD-7 · Pre→Post" value={`${s.pre_gad7} → ${s.post_gad7}`} mono />
          )}
          {!isPartial && (
            <Bento
              label="ΔPHQ-4 / ΔGAD-7"
              value={`${phq4Delta > 0 ? "+" : ""}${phq4Delta} / ${
                gad7Delta > 0 ? "+" : ""
              }${gad7Delta}`}
              mono
            />
          )}
        </div>

        {/* Reflection Coach — GPT-5.6 generates a warm 2-3 sentence
            recap based on the session data. Only appears for complete sessions
            so the partial-exit stays minimal. */}
        {!isPartial && <ReflectionRecap session={s} />}

        {/* Before / After — quiet visual metaphor for what the session
            actually did. Left: the activated body (heart/lungs/vasculature).
            Right: the settled brain. Only shown for complete sessions so
            the partial-stop exit stays minimal. */}
        {!isPartial && (
          <div className="mt-8 fade-up" data-testid="before-after-card">
            <div
              className="rounded-3xl p-5 sm:p-7 border bg-white"
              style={{ borderColor: "var(--hairline, rgba(15,26,46,0.10))" }}
            >
              <div className="text-center mb-5 sm:mb-6">
                <div
                  className="font-mono text-[10px] sm:text-[11px] tracking-[0.28em] uppercase mb-1.5"
                  style={{ color: "var(--accent-red, #BB1B1B)" }}
                >
                  A quiet shift
                </div>
                <h3
                  className="serif text-xl sm:text-2xl text-ink leading-tight"
                  style={{ fontWeight: 700 }}
                >
                  What just happened in your body.
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-6 items-stretch">
                <BeforeAfterFrame
                  label="Before"
                  caption="Activated · alert"
                  img="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/fd12jeq9_SMfKPT-XIKSmbbOnh7JiF.png"
                  imgAlt="The activated body before the session"
                  tone="warm"
                  testId="before-frame"
                />
                <BeforeAfterFrame
                  label="After"
                  caption="Settled · recovering"
                  img="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/917zxnff_brain2.png"
                  imgAlt="The settled brain after the session"
                  tone="cool"
                  testId="after-frame"
                />
              </div>
              <p className="text-center text-[12px] sm:text-[13px] text-mute mt-5 max-w-xl mx-auto leading-relaxed">
                A few minutes of slow, predictable rhythm gives your nervous system permission to step down a gear. This is what the metrics above are tracking.
              </p>
            </div>
          </div>
        )}

        {/* Domain Echo — reinforces the 9-domain framework introduced on the
            Welcome page. Today the patient trained Domain 09 (Autonomic /
            Recovery); we suggest a complementary practice in one adjacent
            domain. Skipped on partial sessions to keep that exit fast. */}
        {!isPartial && (() => {
          const adj = pickAdjacentDomain(s.id);
          return (
            <div className="mt-8 fade-up" data-testid="domain-echo-card">
              <div
                className="rounded-3xl p-6 sm:p-8 border"
                style={{
                  background: "linear-gradient(180deg, #FBF3F3 0%, #FFF 70%)",
                  borderColor: "var(--accent-red, #BB1B1B)",
                  boxShadow: "0 18px 40px -22px rgba(187,27,27,0.18)",
                }}
              >
                <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-6 sm:gap-10 items-center">
                  {/* Home domain — today's training */}
                  <div className="flex items-start gap-4" data-testid="domain-echo-home">
                    <figure
                      className="hidden sm:block rounded-2xl overflow-hidden flex-shrink-0"
                      style={{
                        width: 96,
                        height: 96,
                        background: "linear-gradient(160deg, #2a2418 0%, #4a3826 100%)",
                        boxShadow: "0 10px 24px -12px rgba(15,26,46,0.30)",
                      }}
                    >
                      <img
                        src="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/917zxnff_brain2.png"
                        alt="A calm, glowing brain — the Autonomic / Recovery home domain"
                        className="block w-full h-full object-cover"
                        loading="lazy"
                        data-testid="domain-echo-home-image"
                      />
                    </figure>
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-mono text-[10px] tracking-[0.24em] uppercase mb-1"
                        style={{ color: "var(--accent-red, #BB1B1B)" }}
                      >
                        Today you trained
                      </div>
                      <div
                        className="serif text-2xl text-ink leading-tight"
                        style={{ fontWeight: 700 }}
                      >
                        Domain 09 · Autonomic / Recovery
                      </div>
                      <p className="text-sm text-mute mt-1.5 leading-relaxed">
                        Your nervous system's ability to calm and rebuild resilience.
                        This is the home domain of the Self-Regulation Console.
                      </p>
                    </div>
                  </div>

                  {/* Connector arrow / divider */}
                  <div
                    className="hidden sm:flex flex-col items-center font-mono"
                    style={{ color: "var(--accent-red, #BB1B1B)" }}
                    aria-hidden="true"
                  >
                    <div className="text-2xl">→</div>
                    <div
                      className="text-[9px] tracking-[0.24em] uppercase mt-1"
                      style={{ opacity: 0.7 }}
                    >
                      pair with
                    </div>
                  </div>

                  {/* Adjacent-domain suggestion */}
                  <div className="flex flex-col" data-testid="domain-echo-adjacent">
                    <div
                      className="font-mono text-[10px] tracking-[0.24em] uppercase mb-1"
                      style={{ color: "var(--accent-blue, #153A79)" }}
                    >
                      Try this week
                    </div>
                    <div
                      className="serif text-2xl text-ink leading-tight"
                      style={{ fontWeight: 700 }}
                      data-testid="domain-echo-adjacent-name"
                    >
                      Domain {String(adj.n).padStart(2, "0")} · {adj.name}
                    </div>
                    <p
                      className="text-sm mt-1.5 leading-relaxed text-ink"
                      data-testid="domain-echo-adjacent-tip"
                    >
                      {adj.tip}
                    </p>
                  </div>
                </div>
                <div
                  className="mt-5 pt-4 text-[11px] font-mono tracking-[0.20em] uppercase text-center"
                  style={{
                    color: "var(--ink-soft, #5A6B85)",
                    borderTop: "1px dashed var(--hairline, rgba(15,26,46,0.10))",
                  }}
                >
                  Whole-person wellness · one domain at a time
                </div>
              </div>
            </div>
          );
        })()}

        {/* Personalized "Ask Elara about your session" — pre-loads a
            prompt with the member's actual SUDS shift + engagement so she
            can give a tailored reflection. Available even on partial
            sessions (with the post fields gracefully omitted from the
            prompt). */}
        <AskElaraAboutSession
          mode={s.mode}
          preSuds={s.pre_suds}
          postSuds={s.post_suds}
          sudsDelta={sudsDelta}
          engagement={s.engagement_score}
          duration={s.duration}
          isPartial={isPartial}
        />

        {/* Self-Reported Recovery Score v1 — whole-life composite, distinct
            from the session-level ARS in the bento above. */}
        {!isPartial && <RecoveryScoreCard className="mt-8 fade-up" />}

        {/* Weekly Progress Check-in — the member's answers sync straight to
            their Integrated Wellness record for the wellness team. */}
        {!isPartial && (
          <div
            className="mt-8 fade-up iw-card p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
            style={{ borderTop: "3px solid var(--iw-accent-red)" }}
            data-testid="summary-progress-checkin"
          >
            <div>
              <div
                className="iw-mono uppercase mb-1.5"
                style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}
              >
                Weekly Progress Check-in
              </div>
              <div className="serif text-xl" style={{ color: "var(--iw-primary)" }}>
                Log this week's progress for your wellness team.
              </div>
              <p className="text-sm mt-1.5 leading-relaxed m-0" style={{ color: "var(--iw-body)" }}>
                Ten quick ratings plus your week's practice — about 3 minutes. Your answers
                sync straight to your Integrated Wellness record.
              </p>
            </div>
            <a
              href={PROGRESS_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="iw-btn-primary whitespace-nowrap"
              onClick={markProgressCheckIn}
              data-testid="summary-progress-checkin-btn"
            >
              Log this week <span aria-hidden="true">↗</span>
            </a>
          </div>
        )}

        {s.mode === "A" && (
          <Card className="mt-6 fade-up">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-4">
              Mode A · Tap Telemetry
            </div>
            <div className="grid sm:grid-cols-4 gap-6">
              <Stat label="Tap Count" value={s.tap_count} />
              <Stat label="Tap / min" value={(s.tap_rate || 0).toFixed(1)} />
              <Stat
                label="Alternation"
                value={`${Math.round((s.alternation_pct || 0) * 100)}%`}
              />
              <Stat
                label="Consistency"
                value={`${Math.round((s.consistency_pct || 0) * 100)}%`}
              />
            </div>
          </Card>
        )}

        {s.mode === "B" && (
          <Card className="mt-6 fade-up">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-4">
              Mode B · Hands-Free
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Stat label="Cadence" value={`${(s.cadence_hz || 0).toFixed(2)} Hz`} />
              <Stat label="Completion" value={`${Math.round((s.engagement_score || 0) * 100)}%`} />
            </div>
          </Card>
        )}

        {s.techniques?.length > 0 && (
          <Card className="mt-6 fade-up">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-3">
              Layered Techniques
            </div>
            <div className="flex flex-wrap gap-2">
              {s.techniques.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 rounded-full text-xs border border-[color:var(--hairline)] text-[color:var(--text-secondary)] font-mono"
                >
                  {t}
                </span>
              ))}
            </div>
          </Card>
        )}

        <Card className="mt-6 fade-up" data-testid="data-pipeline-card">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-3">
            Data Writeback
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <PipelineDot label="MongoDB" ok />
            <PipelineDot
              label={notion.enabled ? "Notion" : "Notion (disabled)"}
              ok={notion.success}
              warn={notion.enabled && !notion.success}
            />
            <PipelineDot label="Healthie (planned)" dim />
            <PipelineDot label="Spruce SMS (planned)" dim />
          </div>
          {notion.enabled && !notion.success && notion.error && (
            <div className="mt-3 text-xs font-mono text-[color:var(--warning)]">
              Notion write failed: {notion.error}
            </div>
          )}
          {!notion.enabled && (
            <div className="mt-3 text-xs font-mono text-[color:var(--text-secondary)]">
              Add NOTION_TOKEN & NOTION_DATABASE_ID to /app/backend/.env to enable Notion writeback.
            </div>
          )}
        </Card>

        {(s.started_at || s.ended_at) && (
          <WearableWindowCard startedAt={s.started_at} endedAt={s.ended_at} />
        )}

        <div className="mt-10 flex items-center gap-4">
          <button
            type="button"
            className="btn-primary"
            onClick={onRestart}
            data-testid="summary-restart-btn"
          >
            Begin Another Session ↻
          </button>
        </div>
      </div>
    </Aurora>
  );
}

function ReflectionRecap({ session }) {
  const [state, setState] = useState("idle");
  const [recap, setRecap] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | saved-local | error
  const [saveError, setSaveError] = useState("");

  const generate = useCallback(async () => {
    setState("loading");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reflection/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pre_suds: session.pre_suds,
            post_suds: session.post_suds,
            pre_phq4: session.pre_phq4,
            post_phq4: session.post_phq4,
            pre_gad7: session.pre_gad7,
            post_gad7: session.post_gad7,
            duration_seconds: session.duration_seconds,
            mode: session.mode,
            reflection:
              session.notes && session.notes.includes("[REFLECTION]")
                ? session.notes.replace(/^.*\[REFLECTION\]\s*/, "").split(" · ")[0]
                : null,
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecap(data.recap || "");
      setState("done");
      setSaveState("idle");
      setSaveError("");
    } catch (err) {
      setState("error");
    }
  }, [session]);

  const saveToNotion = useCallback(async () => {
    if (!recap || saveState === "saving") return;
    setSaveState("saving");
    setSaveError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sessions/${session.id}/notes`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: recap, label: "REFLECTION" }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const notion = data.notion || {};
      if (notion.enabled && !notion.success) {
        // Mongo persist DID succeed (HTTP 200); only the Notion mirror
        // failed. Communicate that as a soft "saved locally" state so the
        // member isn't misled into re-clicking.
        setSaveState("saved-local");
        setSaveError(notion.error || "Notion sync temporarily unavailable.");
        return;
      }
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      setSaveError(err.message || "Save failed.");
    }
  }, [recap, saveState, session.id]);

  return (
    <div
      className="mt-8 fade-up"
      data-testid="reflection-recap-card"
      style={{
        border: "1px solid var(--iw-border)",
        borderTop: "3px solid var(--iw-accent-red)",
        borderRadius: "2px",
        background: "var(--iw-surface)",
        padding: "1.75rem",
      }}
    >
      <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div
            className="font-mono uppercase mb-1"
            style={{
              color: "var(--iw-accent-red)",
              fontSize: "10px",
              letterSpacing: "0.24em",
              fontWeight: 600,
            }}
          >
            Reflection Coach · GPT-5.6
          </div>
          <h3
            className="serif text-xl sm:text-2xl leading-tight"
            style={{ color: "var(--iw-primary)", fontWeight: 700 }}
          >
            A warm recap of what your body just did.
          </h3>
        </div>
        {state === "idle" && (
          <button
            type="button"
            onClick={generate}
            className="iw-btn-primary"
            data-testid="reflection-generate-btn"
          >
            Generate recap <span aria-hidden="true">↗</span>
          </button>
        )}
        {state === "loading" && (
          <span
            className="font-mono uppercase"
            style={{
              color: "var(--iw-muted)",
              fontSize: "10px",
              letterSpacing: "0.20em",
              fontWeight: 600,
            }}
            data-testid="reflection-loading"
          >
            Thinking · · ·
          </span>
        )}
      </div>
      {state === "done" && recap && (
        <>
          <p
            className="text-base sm:text-lg leading-relaxed"
            style={{ color: "var(--iw-body)" }}
            data-testid="reflection-recap-text"
          >
            {recap}
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <ListenButton text={recap} size={34} label="Listen" testId="reflection-listen-btn" />
            {saveState !== "saved" && saveState !== "saved-local" && (
              <button
                type="button"
                onClick={saveToNotion}
                disabled={saveState === "saving"}
                className="iw-btn-primary"
                style={{
                  opacity: saveState === "saving" ? 0.5 : 1,
                  cursor: saveState === "saving" ? "wait" : "pointer",
                }}
                data-testid="reflection-save-notion-btn"
              >
                {SAVE_LABELS[saveState] || "Save to Notion ↗"}
              </button>
            )}
            {saveState === "saved" && (
              <span
                className="font-mono uppercase inline-flex items-center gap-2"
                style={{
                  color: "var(--iw-blue, #153A79)",
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
                data-testid="reflection-save-notion-confirm"
              >
                ✓ Saved to your Notion session record
              </span>
            )}
            {saveState === "saved-local" && (
              <div
                className="flex flex-col gap-0.5"
                data-testid="reflection-save-notion-local"
              >
                <span
                  className="font-mono uppercase inline-flex items-center gap-2"
                  style={{
                    color: "var(--iw-blue, #153A79)",
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    fontWeight: 600,
                  }}
                >
                  ✓ Saved to your session record
                </span>
                <span
                  className="text-[11px]"
                  style={{ color: "var(--iw-muted)" }}
                >
                  Notion sync is temporarily unavailable — your wellness team will still see this on their side once it reconnects.
                </span>
              </div>
            )}
            {saveState === "error" && saveError && (
              <span
                className="text-xs"
                style={{ color: "var(--iw-accent-red)" }}
                data-testid="reflection-save-notion-error"
                role="alert"
              >
                {saveError}
              </span>
            )}
          </div>
        </>
      )}
      {state === "error" && (
        <p
          className="text-sm"
          style={{ color: "var(--iw-accent-red)" }}
          data-testid="reflection-recap-error"
          role="alert"
        >
          The recap couldn&apos;t be generated right now — please try again in a moment.
        </p>
      )}
      {state === "idle" && (
        <p
          className="text-sm leading-relaxed"
          style={{ color: "var(--iw-muted)" }}
        >
          Your AI coach will read your session numbers and (if provided) your own
          reflection, then write 2–3 gentle sentences you can save. Non-medical
          wellness coaching only.
        </p>
      )}
    </div>
  );
}

function Bento({ label, value, accent = false, danger = false, mono = false }) {
  return (
    <Card className="!p-6">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)] mb-2">
        {label}
      </div>
      <div
        className={`${mono ? "font-mono" : ""} text-3xl tracking-wider font-light`}
        style={{
          color: statValueColor(accent, danger),
          textShadow: accent ? "0 0 18px rgba(0,191,255,0.5)" : "none",
        }}
      >
        {value}
      </div>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)] mb-1">
        {label}
      </div>
      <div className="font-mono text-2xl text-ink tracking-wider font-light">
        {value}
      </div>
    </div>
  );
}

function BeforeAfterFrame({ label, caption, img, imgAlt, tone, testId }) {
  // tone: 'warm' = activated state (subtle red tint); 'cool' = settled state
  // (subtle blue tint). Color choice mirrors the rest of the brand palette.
  const accent =
    tone === "warm" ? "var(--accent-red, #BB1B1B)" : "var(--accent-blue, #153A79)";
  const bg =
    tone === "warm"
      ? "linear-gradient(160deg, #3a1a14 0%, #5a2a1f 100%)"
      : "linear-gradient(160deg, #0f1a2e 0%, #1c2f4d 100%)";
  return (
    <figure
      className="relative rounded-2xl overflow-hidden"
      style={{
        aspectRatio: "1 / 1",
        background: bg,
        boxShadow: "0 16px 36px -22px rgba(15,26,46,0.30)",
      }}
      data-testid={testId}
    >
      <img
        src={img}
        alt={imgAlt}
        className="block w-full h-full object-cover"
        loading="lazy"
        style={{ opacity: tone === "warm" ? 0.85 : 0.92 }}
      />
      <div
        className="absolute top-3 left-3 sm:top-4 sm:left-4 px-2 sm:px-2.5 py-1 rounded-full font-mono text-[9px] sm:text-[10px] tracking-[0.28em] uppercase"
        style={{
          background: "rgba(255,255,255,0.92)",
          color: accent,
        }}
      >
        {label}
      </div>
      <figcaption
        className="absolute bottom-3 left-3 right-3 font-mono text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-center"
        style={{ color: "rgba(255,255,255,0.85)" }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}



function PipelineDot({ label, ok = false, warn = false, dim = false }) {
  let color = "rgba(255,255,255,0.15)";
  if (ok) color = "var(--success)";
  if (warn) color = "var(--warning)";
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-2.5 h-2.5 rounded-full"
        style={{
          background: color,
          boxShadow: ok || warn ? `0 0 12px ${color}` : "none",
          opacity: dim ? 0.4 : 1,
        }}
      />
      <div
        className={`font-mono text-[11px] tracking-widest uppercase ${
          dim ? "text-[color:var(--text-secondary)]" : "text-ink"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function WearableWindowCard({ startedAt, endedAt }) {
  const [copied, setCopied] = React.useState(false);
  const startD = startedAt ? new Date(startedAt) : null;
  const endD = endedAt ? new Date(endedAt) : null;
  const baselineStart = startD ? new Date(startD.getTime() - 120 * 1000) : null;
  const baselineEnd = startD ? new Date(startD.getTime() - 60 * 1000) : null;
  const recoveryStart = endD ? new Date(endD.getTime() + 60 * 1000) : null;
  const recoveryEnd = endD ? new Date(endD.getTime() + 300 * 1000) : null;

  const fmt = (d) =>
    d
      ? d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        })
      : "—";
  const iso = (d) => (d ? d.toISOString() : "—");

  const copyAll = async () => {
    const text = [
      `BLS Session Timestamps`,
      `Started:           ${iso(startD)}`,
      `Ended:             ${iso(endD)}`,
      `Pre-session baseline window:   ${iso(baselineStart)}  →  ${iso(baselineEnd)}`,
      `Post-session recovery window:  ${iso(recoveryStart)}  →  ${iso(recoveryEnd)}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // Clipboard API requires a secure context or fails in some browsers.
      // Non-fatal — user can manually select the text.
      console.warn("[SessionSummary] clipboard write failed:", err);
    }
  };

  return (
    <Card className="mt-6 fade-up" data-testid="wearable-window-card">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-1">
            Wearable Data Reference
          </div>
          <div className="text-ink text-base font-light">
            Use these timestamps to pull HR / HRV from Apple Health, Google Fit, Whoop, Oura, etc.
          </div>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={copyAll}
          data-testid="wearable-copy-btn"
        >
          ⧉ Copy timestamps
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <TimeRow label="Session Start" local={fmt(startD)} isoStr={iso(startD)} testid="wearable-started-at" />
        <TimeRow label="Session End" local={fmt(endD)} isoStr={iso(endD)} testid="wearable-ended-at" />
        <TimeRow
          label="Pre-Session Baseline (–120s → –60s)"
          local={`${fmt(baselineStart)}  →  ${fmt(baselineEnd)}`}
          isoStr={`${iso(baselineStart)}  →  ${iso(baselineEnd)}`}
          testid="wearable-baseline-window"
        />
        <TimeRow
          label="Post-Session Recovery (+60s → +300s)"
          local={`${fmt(recoveryStart)}  →  ${fmt(recoveryEnd)}`}
          isoStr={`${iso(recoveryStart)}  →  ${iso(recoveryEnd)}`}
          testid="wearable-recovery-window"
        />
      </div>
      <div className="mt-4 text-xs font-mono text-[color:var(--text-secondary)] leading-relaxed">
        These windows match the build-spec biometric capture protocol (pre-baseline 60–120s, post-recovery 60–300s) and can be fed into a Parasympathetic Recovery Index (PRI) computation once HR / HRV ingestion is wired.
      </div>
    </Card>
  );
}

function TimeRow({ label, local, isoStr, testid }) {
  return (
    <div
      className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--bg-tint)] p-4"
      data-testid={testid}
    >
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)] mb-1">
        {label}
      </div>
      <div className="text-ink text-sm">{local}</div>
      <div className="font-mono text-[11px] text-[color:var(--accent-cyan)] mt-1 break-all">
        {isoStr}
      </div>
    </div>
  );
}


/**
 * AskElaraAboutSession — clickable card on the Session Summary screen
 * that wakes Elara (the program guide) pre-loaded with a personalized
 * prompt drawing on the actual SUDS shift + engagement % + mode +
 * duration. Designed to be the highest-attention "what just happened?"
 * affordance, surfaced immediately after the metrics row.
 */
function AskElaraAboutSession({
  mode,
  preSuds,
  postSuds,
  sudsDelta,
  engagement,
  duration,
  isPartial,
}) {
  const [toast, setToast] = useState(null);

  // Build a one-sentence "what just happened" recap to render inside the
  // card, plus a prompt that carries the same data into Elara.
  const summary = buildSessionRecap({
    mode,
    preSuds,
    postSuds,
    sudsDelta,
    engagement,
    duration,
    isPartial,
  });

  const handleAsk = useCallback(async () => {
    const { opened, copied } = await wakeElara(summary.prompt);
    setToast({
      opened,
      copied,
      pasteShortcut: detectPasteShortcutSimple(),
      stamp: Date.now(),
    });
  }, [summary.prompt]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 8500);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <>
      <section
        className="mt-8 fade-up iw-card relative overflow-hidden p-6 sm:p-8"
        style={{ borderTop: "3px solid var(--iw-accent-red)" }}
        data-testid="ask-elara-session"
      >
        <div className="grid md:grid-cols-[1fr_auto] gap-5 sm:gap-7 items-center">
          <div>
            <div
              className="iw-mono uppercase mb-2"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "11px",
                letterSpacing: "0.24em",
                fontWeight: 600,
              }}
            >
              Ask Elara · Personalized
            </div>
            <h3
              className="serif leading-tight"
              style={{
                color: "var(--iw-primary)",
                fontWeight: 700,
                fontSize: "clamp(1.4rem, 2.6vw, 1.85rem)",
              }}
            >
              Want help understanding what just happened?
            </h3>
            <p
              className="mt-2 text-[14.5px] leading-relaxed max-w-2xl"
              style={{ color: "var(--iw-body)" }}
              data-testid="ask-elara-session-recap"
            >
              {summary.recap}
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <button
              type="button"
              onClick={handleAsk}
              className="iw-btn-primary"
              data-testid="ask-elara-session-btn"
            >
              Ask Elara about this <span aria-hidden="true">↗</span>
            </button>
            <span
              className="iw-mono uppercase"
              style={{
                color: "var(--iw-muted)",
                fontSize: "10px",
                letterSpacing: "0.20em",
                fontWeight: 600,
              }}
            >
              Your numbers travel with you ↘
            </span>
          </div>
        </div>
      </section>
      <SimpleAgentToast
        toast={toast}
        agentName="Elara"
        prompt={summary.prompt}
        onClose={() => setToast(null)}
      />
    </>
  );
}

// Pure helper — builds the visible recap line + the (longer, structured)
// prompt that goes to Elara. Kept outside the component so it's trivially
// unit-testable down the road.
function buildSessionRecap({
  mode,
  preSuds,
  postSuds,
  sudsDelta,
  engagement,
  duration,
  isPartial,
}) {
  const modeLabel = mode === "A" ? "Mode A · Tap-Based" : "Mode B · Hands-Free";
  const minutes = duration ? Math.max(1, Math.round(duration / 60)) : null;
  const engagementPct =
    typeof engagement === "number" ? Math.round(engagement * 100) : null;
  const hasPostSuds = postSuds !== null && postSuds !== undefined;

  // ── Visible one-line recap ───────────────────────────────────────────
  let recap;
  if (isPartial) {
    recap = `You ran a partial ${modeLabel} session${minutes ? ` for about ${minutes} min` : ""}${
      engagementPct !== null ? ` at ${engagementPct}% engagement` : ""
    }. Elara can help you interpret what you captured and decide what to try next.`;
  } else if (hasPostSuds) {
    let deltaText;
    if (sudsDelta < 0) {
      deltaText = `your distress dropped from ${preSuds} → ${postSuds} (Δ ${sudsDelta})`;
    } else if (sudsDelta === 0) {
      deltaText = `your distress held steady at ${preSuds}`;
    } else {
      deltaText = `your distress rose from ${preSuds} → ${postSuds} (Δ +${sudsDelta})`;
    }
    recap = `In this ${modeLabel} session${minutes ? ` (${minutes} min)` : ""}, ${deltaText}${
      engagementPct !== null ? ` with ${engagementPct}% engagement` : ""
    }. Elara can walk you through what that means and what to try next.`;
  } else {
    recap = `You completed a ${modeLabel} session${
      minutes ? ` of ${minutes} min` : ""
    }${
      engagementPct !== null ? ` at ${engagementPct}% engagement` : ""
    }. Elara can talk through what to look for and what to try next.`;
  }

  // ── Structured prompt that travels to Elara ──────────────────────────
  const bullets = [];
  bullets.push(`Mode: ${modeLabel}`);
  if (minutes) bullets.push(`Duration: ~${minutes} min`);
  if (preSuds !== null && preSuds !== undefined) {
    bullets.push(
      hasPostSuds
        ? `SUDS: ${preSuds} (pre) → ${postSuds} (post) · Δ ${
            sudsDelta > 0 ? "+" : ""
          }${sudsDelta}`
        : `SUDS pre: ${preSuds}`
    );
  }
  if (engagementPct !== null) bullets.push(`Engagement: ${engagementPct}%`);
  if (isPartial) bullets.push("This session was stopped early (partial).");

  const prompt = [
    "Help me understand my session summary. Here are my numbers:",
    ...bullets.map((b) => `• ${b}`),
    "",
    "What does this tell you about how my nervous system responded, and what should I try next time?",
  ].join("\n");

  return { recap, prompt };
}

// Lightweight platform detector — duplicated locally so this component
// doesn't depend on Welcome.jsx.
function detectPasteShortcutSimple() {
  if (typeof navigator === "undefined") return "Ctrl + V";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toLowerCase();
  const isApple =
    /mac|iphone|ipad|ipod/.test(platform) || /mac|iphone|ipad/.test(ua);
  return isApple ? "\u2318 V" : "Ctrl + V";
}

/**
 * SimpleAgentToast — slim version of AgentPromptToast used on the Session
 * Summary screen. Same visual language; takes the prompt + agent name as
 * direct props so it can live anywhere without threading state through
 * the Welcome shell.
 */
function SimpleAgentToast({ toast, agentName, prompt, onClose }) {
  if (!toast) return null;
  const { opened, copied, pasteShortcut } = toast;
  const headline = opened
    ? `${agentName} is open · your session`
    : `Opening ${agentName} in a new tab · your session`;
  const pasteHint = copied
    ? `Paste it (${pasteShortcut})`
    : `Type or paste it (${pasteShortcut})`;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="ask-elara-session-toast"
      className="iw-maya-toast"
    >
      <div
        className="iw-mono uppercase mb-2"
        style={{
          color: "var(--iw-accent-red)",
          fontSize: "10px",
          letterSpacing: "0.22em",
          fontWeight: 700,
        }}
      >
        {headline}
      </div>
      <div
        className="text-[13px] leading-relaxed mb-3"
        style={{ color: "var(--iw-primary)", fontWeight: 600 }}
      >
        {copied
          ? "We copied a personalized prompt for you:"
          : "Use this personalized prompt with Elara:"}
      </div>
      <pre
        className="iw-mono text-[11.5px] leading-relaxed mb-3 whitespace-pre-wrap m-0"
        style={{
          color: "var(--iw-primary)",
          background: "var(--iw-blue-tint)",
          border: "1px solid var(--iw-border)",
          padding: "0.6rem 0.7rem",
          borderRadius: "2px",
          letterSpacing: "0.01em",
          maxHeight: 180,
          overflowY: "auto",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        }}
      >
        {prompt}
      </pre>
      <ol className="space-y-1.5 list-none m-0 p-0">
        <li className="flex items-start gap-2.5 leading-snug">
          <span
            className="iw-mono flex-shrink-0 mt-0.5"
            style={{ color: "var(--iw-accent-red)", fontSize: "11px", fontWeight: 700, width: 14 }}
          >
            1
          </span>
          <span className="text-[12.5px]" style={{ color: "var(--iw-body)" }}>
            Click the chat input that says “Type here” in {agentName}’s window.
          </span>
        </li>
        <li className="flex items-start gap-2.5 leading-snug">
          <span
            className="iw-mono flex-shrink-0 mt-0.5"
            style={{ color: "var(--iw-accent-red)", fontSize: "11px", fontWeight: 700, width: 14 }}
          >
            2
          </span>
          <span className="text-[12.5px]" style={{ color: "var(--iw-body)" }}>
            {pasteHint}
          </span>
        </li>
        <li className="flex items-start gap-2.5 leading-snug">
          <span
            className="iw-mono flex-shrink-0 mt-0.5"
            style={{ color: "var(--iw-accent-red)", fontSize: "11px", fontWeight: 700, width: 14 }}
          >
            3
          </span>
          <span className="text-[12.5px]" style={{ color: "var(--iw-body)" }}>
            Press the send button (or Enter).
          </span>
        </li>
      </ol>
      <button
        type="button"
        onClick={onClose}
        className="iw-mono uppercase mt-3 inline-flex items-center gap-1.5"
        style={{
          color: "var(--iw-muted)",
          fontSize: "10px",
          letterSpacing: "0.18em",
          fontWeight: 600,
          background: "none",
          border: "none",
          cursor: "pointer",
        }}
        data-testid="ask-elara-session-toast-close"
      >
        Got it ✕
      </button>
    </div>
  );
}
